import styled from '@emotion/styled';

import type { Theme as CobrainerTheme } from '../../../theme';
import type { CSSObject } from '@emotion/styled';
import type { FunctionComponent, ReactNode } from 'react';
import type { ButtonHTMLAttributes, MouseEvent } from 'react';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    readonly children: ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly disabledVariant?: 'dark' | 'light';
    readonly iconOnly?: boolean;
    readonly isActive?: boolean;
    readonly onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    readonly size?: 'm' | 'sm' | 'xs';
    readonly type?: 'button' | 'reset' | 'submit';
    readonly variant?: 'default' | 'destructive' | 'ghost' | 'link' | 'raised' | 'round' | 'secondary' | 'subtle';
};

const getPadding = (size: ButtonProps['size'], iconOnly: boolean | undefined, theme: CobrainerTheme): string => {
    const isIconOnly = iconOnly ?? false;

    switch (size) {
        case 'xs':
            return isIconOnly ? theme.spacing.xs : `${theme.spacing.xs} ${theme.spacing.s}`;
        case 'sm':
            return isIconOnly
                ? `calc(${theme.spacing.m} + ${theme.spacing.xxs})`
                : `${theme.spacing.m} ${theme.spacing.xl}`;
        case 'm':
            return isIconOnly
                ? `calc(${theme.spacing.l} + ${theme.spacing.xxs})`
                : `${theme.spacing.l} ${theme.spacing.xl}`;
        default:
            return isIconOnly
                ? `calc(${theme.spacing.m} + ${theme.spacing.xxs})`
                : `${theme.spacing.m} ${theme.spacing.xxl}`;
    }
};

const getTypography = (size: ButtonProps['size'], theme: CobrainerTheme): CSSObject => {
    switch (size) {
        case 'xs':
            return {
                ...theme.typography.body.XXS.semibold,
            };
        case 'sm':
            return {
                fontSize: theme.typography.body.S.regular.fontSize,
                lineHeight: theme.typography.body.S.regular.lineHeight,
            };
        case 'm':
            return {
                fontSize: theme.typography.body.S.regular.fontSize,
                lineHeight: theme.typography.body.S.regular.lineHeight,
            };
        default:
            return {
                fontSize: theme.typography.body.S.regular.fontSize,
                lineHeight: theme.typography.body.S.regular.lineHeight,
            };
    }
};

const getDefaultStyles = (
    disabled: boolean,
    disabledVariant: ButtonProps['disabledVariant'],
    theme: CobrainerTheme,
): CSSObject => {
    const disabledBgColor = disabledVariant === 'dark' ? theme.colors.black[900] : theme.colors.gray[500];

    return {
        '&:hover': {
            backgroundColor: disabled ? disabledBgColor : theme.colors.black[800],
        },
        backgroundColor: disabled ? disabledBgColor : theme.colors.black[900],
        color: theme.colors.white[200],
        transition: 'none',
    };
};

const getSecondaryStyles = (disabled: boolean, isActive: boolean, theme: CobrainerTheme): CSSObject => ({
    '&:hover': {
        backgroundColor: disabled ? theme.colors.white[200] : theme.colors.gray[100],
        borderColor: theme.colors.gray[500],
    },
    backgroundColor: theme.colors.white[200],
    border: `1px solid ${theme.colors.gray[400]}`,
    color: isActive ? theme.colors.orange[800] : theme.colors.gray[900],
});

const getDestructiveStyles = (theme: CobrainerTheme): CSSObject => ({
    backgroundColor: theme.colors.error[600],
    color: theme.colors.white[200],
});

const getGhostStyles = (disabled: boolean, isActive: boolean, theme: CobrainerTheme): CSSObject => ({
    '&:hover': {
        backgroundColor: disabled ? 'transparent' : theme.colors.gray[100],
    },
    backgroundColor: 'transparent',
    color: isActive ? theme.colors.orange[800] : theme.colors.gray[900],
    transition: 'none',
});

const getActiveSubtleStyles = (theme: CobrainerTheme): CSSObject => ({
    backgroundColor: theme.colors.orange[100],
    color: theme.colors.orange[800],
});

const getSubtleStyles = (disabled: boolean, isActive: boolean, theme: CobrainerTheme): CSSObject => {
    const baseStyles: CSSObject = {
        '&:hover': {
            backgroundColor: disabled ? theme.colors.gray[100] : theme.colors.gray[300],
        },
        backgroundColor: theme.colors.gray[100],
        boxShadow: theme.shadows.small,
        color: theme.colors.gray[900],
    };

    if (isActive) {
        const activeStyles = getActiveSubtleStyles(theme);

        return {
            ...baseStyles,
            ...activeStyles,
            '&:hover': {
                ...activeStyles,
            },
        };
    }

    return baseStyles;
};

const getRaisedStyles = (disabled: boolean, theme: CobrainerTheme): CSSObject => ({
    '&:hover': {
        backgroundColor: disabled ? theme.colors.gray[500] : theme.colors.gray[200],
        boxShadow: theme.shadows.medium,
        opacity: 1,
    },
    backgroundColor: disabled ? theme.colors.gray[200] : theme.colors.gray[100],
    borderRadius: theme.borderRadius.xs,
    boxShadow: disabled ? 'none' : theme.shadows.small,
    color: theme.colors.gray[800],
});

const getLinkStyles = (disabled: boolean, theme: CobrainerTheme): CSSObject => ({
    '&:hover': {
        color: disabled ? theme.colors.gray[500] : theme.colors.black[800],
        textDecoration: 'none',
    },
    backgroundColor: 'transparent',
    color: disabled ? theme.colors.gray[500] : theme.colors.black[900],
    padding: `${theme.spacing.xs} ${theme.spacing.s}`,
    textDecoration: 'none',
});

const getRoundDimensions = (
    size: ButtonProps['size'],
): { readonly borderRadius: string; readonly buttonSize: string; readonly iconSize: string } => {
    switch (size) {
        case 'xs':
            return { borderRadius: '1.5rem', buttonSize: '1.5rem', iconSize: '0.625rem' };
        case 'sm':
            return { borderRadius: '0.375rem', buttonSize: '1.75rem', iconSize: '0.75rem' };
        case 'm':
            return { borderRadius: '0.5rem', buttonSize: '2.5rem', iconSize: '1rem' };
        default:
            return { borderRadius: '0.375rem', buttonSize: '1.75rem', iconSize: '0.75rem' };
    }
};

const getRoundStyles = (
    disabled: boolean,
    iconOnly: boolean,
    size: ButtonProps['size'],
    theme: CobrainerTheme,
): CSSObject => {
    const { borderRadius, buttonSize, iconSize } = getRoundDimensions(size);

    const baseStyles: CSSObject = {
        '&:hover': {
            backgroundColor: disabled ? theme.colors.gray[500] : theme.colors.gray[200],
            boxShadow: theme.shadows.medium,
            opacity: 1,
        },
        backgroundColor: disabled ? theme.colors.gray[200] : theme.colors.gray[100],
        borderRadius,
        boxShadow: disabled ? 'none' : theme.shadows.small,
        color: theme.colors.gray[800],
    };

    if (iconOnly) {
        return {
            ...baseStyles,
            '& > svg': {
                height: iconSize,
                width: iconSize,
            },
            height: buttonSize,
            padding: 0,
            width: buttonSize,
        };
    }

    return baseStyles;
};

const getVariantStyles = (
    disabled: boolean,
    disabledVariant: ButtonProps['disabledVariant'],
    iconOnly: boolean,
    isActive: boolean,
    size: ButtonProps['size'],
    theme: CobrainerTheme,
    variant: ButtonProps['variant'],
): CSSObject => {
    switch (variant) {
        case 'default':
            return getDefaultStyles(disabled, disabledVariant, theme);
        case 'secondary':
            return getSecondaryStyles(disabled, isActive, theme);
        case 'destructive':
            return getDestructiveStyles(theme);
        case 'ghost':
            return getGhostStyles(disabled, isActive, theme);
        case 'subtle':
            return getSubtleStyles(disabled, isActive, theme);
        case 'raised':
            return getRaisedStyles(disabled, theme);
        case 'link':
            return getLinkStyles(disabled, theme);
        case 'round':
            return getRoundStyles(disabled, iconOnly, size, theme);
        default:
            return {};
    }
};

const StyledButton = styled.button<ButtonProps>(
    ({ disabled = false, disabledVariant, iconOnly = false, isActive = false, size, theme, variant }) => ({
        '& > svg': {
            height: '1rem',
            width: '1rem',
        },
        '&:hover': {
            opacity: disabled ? 0.5 : 0.8,
        },
        '&[disabled]': {
            opacity: 0.5,
        },
        alignItems: 'center',
        border: 'none',
        borderRadius: theme.borderRadius.l,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        fontFamily: theme.typography.body.S.regular.fontFamily,
        fontWeight: theme.typography.body.S.medium.fontWeight,
        gap: theme.spacing.s,
        justifyContent: 'center',
        opacity: disabled ? 0.5 : 1,
        outline: 'none',
        padding: getPadding(size, iconOnly, theme),
        textDecoration: 'none',
        transition: 'all 0.2s ease-in-out',
        ...getTypography(size, theme),
        ...getVariantStyles(disabled, disabledVariant, iconOnly, isActive, size, theme, variant),
    }),
);

export const Button: FunctionComponent<ButtonProps> = ({
    children,
    className = '',
    disabled = false,
    disabledVariant,
    iconOnly = false,
    isActive = false,
    onClick,
    size,
    type = 'button',
    variant = 'default',
    ...props
}) => {
    return (
        <StyledButton
            className={className}
            disabled={disabled}
            disabledVariant={disabledVariant}
            iconOnly={iconOnly}
            isActive={isActive}
            size={size}
            type={type}
            variant={variant}
            onClick={onClick}
            {...props}
        >
            {children}
        </StyledButton>
    );
};
